// src/components/admin/nornsweave/AidesTab.js
import React, { useState } from 'react';
import { useAdminNornsWeave } from './AdminNornsWeaveContext';
import { formatTime12 } from '../../TimePicker';
import TimePicker from '../../TimePicker';

function AidesTab({ onAddAide, onEditAide, onDeleteAide, onSaveSettings }) {
  const { aides, settingsForm, setSettingsForm } = useAdminNornsWeave();

  const [newBlock, setNewBlock] = useState({
    name: '',
    startTime: '',
    endTime: '',
    library: 'elementary',
    scheduleType: 'regular'
  });

  // Collapsible section states (collapsed by default)
  const [expandedSections, setExpandedSections] = useState({
    scheduleBlocks: false,
    aides: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const aideScheduleBlocks = settingsForm.aideScheduleBlocks || [];
  const regularBlocks = aideScheduleBlocks.filter(b => b.scheduleType !== 'mass_day');
  const massDayBlocks = aideScheduleBlocks.filter(b => b.scheduleType === 'mass_day');

  const handleAddBlock = () => {
    if (!newBlock.name || !newBlock.startTime || !newBlock.endTime) {
      alert('Please fill in block name, start time, and end time');
      return;
    }
    const updatedBlocks = [...aideScheduleBlocks, { ...newBlock, id: Date.now().toString() }];
    const updatedSettings = { ...settingsForm, aideScheduleBlocks: updatedBlocks };
    setSettingsForm(updatedSettings);
    onSaveSettings(updatedSettings);
    setNewBlock({ name: '', startTime: '', endTime: '', library: 'elementary', scheduleType: 'regular' });
  };

  const handleDeleteBlock = (blockId) => {
    if (!window.confirm('Delete this schedule block? Any aides assigned to it will need to be reassigned.')) return;
    const updatedBlocks = aideScheduleBlocks.filter(b => b.id !== blockId);
    const updatedSettings = { ...settingsForm, aideScheduleBlocks: updatedBlocks };
    setSettingsForm(updatedSettings);
    onSaveSettings(updatedSettings);
  };

  // Get block name by ID
  const getBlockName = (blockId) => {
    const block = aideScheduleBlocks.find(b => b.id === blockId);
    return block ? block.name : 'Unknown';
  };

  // Get block details by ID
  const getBlockDetails = (blockId) => {
    const block = aideScheduleBlocks.find(b => b.id === blockId);
    if (!block) return null;
    return block;
  };

  return (
    <div className="space-y-6">
      {/* Schedule Blocks Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div
          className="bg-teal-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-teal-100 transition-colors"
          onClick={() => toggleSection('scheduleBlocks')}
        >
          <div>
            <h2 className="text-xl font-bold text-teal-800 flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.scheduleBlocks ? 'rotate-90' : ''}`}>▶</span>
              <span>⏰</span> Aide Schedule Blocks
              <span className="text-sm font-normal text-teal-600">({aideScheduleBlocks.length})</span>
            </h2>
            <p className="text-teal-600 text-sm mt-1">Define time blocks, then assign aides to them</p>
          </div>
        </div>

        {expandedSections.scheduleBlocks && (
        <div className="p-6">
        {/* Existing Blocks */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Regular Blocks */}
          <div>
            <h3 className="font-semibold text-teal-700 mb-3 flex items-center gap-2">
              <span className="w-3 h-3 bg-green-100 border border-green-300 rounded"></span>
              Regular Day Blocks
            </h3>
            {regularBlocks.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No regular blocks defined</p>
            ) : (
              <div className="space-y-2">
                {regularBlocks.map(block => (
                  <div key={block.id} className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
                    <div>
                      <span className="font-medium text-teal-800">{block.name}</span>
                      <span className="text-sm text-teal-600 ml-2">
                        {formatTime12(block.startTime)} - {formatTime12(block.endTime)}
                      </span>
                      <span className="text-xs text-teal-500 ml-2">
                        ({block.library === 'elementary' ? 'Elem' : block.library === 'middle' ? 'MS' : 'Both'})
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteBlock(block.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mass Day Blocks */}
          <div>
            <h3 className="font-semibold text-violet-700 mb-3 flex items-center gap-2">
              <span className="w-3 h-3 bg-violet-100 border border-violet-300 rounded"></span>
              Mass Day Blocks
            </h3>
            {massDayBlocks.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No mass day blocks defined (will use regular)</p>
            ) : (
              <div className="space-y-2">
                {massDayBlocks.map(block => (
                  <div key={block.id} className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                    <div>
                      <span className="font-medium text-violet-800">{block.name}</span>
                      <span className="text-sm text-violet-600 ml-2">
                        {formatTime12(block.startTime)} - {formatTime12(block.endTime)}
                      </span>
                      <span className="text-xs text-violet-500 ml-2">
                        ({block.library === 'elementary' ? 'Elem' : block.library === 'middle' ? 'MS' : 'Both'})
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteBlock(block.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Add New Block Form */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <h4 className="font-medium text-slate-700 mb-3">Add New Block</h4>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <input
              type="text"
              placeholder="Block Name"
              className="col-span-2 md:col-span-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={newBlock.name}
              onChange={(e) => setNewBlock({ ...newBlock, name: e.target.value })}
            />
            <TimePicker
              label=""
              value={newBlock.startTime}
              onChange={(val) => setNewBlock({ ...newBlock, startTime: val })}
            />
            <TimePicker
              label=""
              value={newBlock.endTime}
              onChange={(val) => setNewBlock({ ...newBlock, endTime: val })}
            />
            <select
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={newBlock.library}
              onChange={(e) => setNewBlock({ ...newBlock, library: e.target.value })}
            >
              <option value="elementary">Elementary</option>
              <option value="middle">Middle School</option>
              <option value="both">Both</option>
            </select>
            <select
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={newBlock.scheduleType}
              onChange={(e) => setNewBlock({ ...newBlock, scheduleType: e.target.value })}
            >
              <option value="regular">Regular Day</option>
              <option value="mass_day">Mass Day</option>
            </select>
            <button
              onClick={handleAddBlock}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium"
            >
              + Add Block
            </button>
          </div>
        </div>
        </div>
        )}
      </div>

      {/* Aides Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div
          className="bg-blue-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => toggleSection('aides')}
        >
          <div>
            <h2 className="text-xl font-bold text-blue-800 flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.aides ? 'rotate-90' : ''}`}>▶</span>
              <span>🙋</span> Library Aides
              <span className="text-sm font-normal text-blue-600">({aides.length})</span>
            </h2>
            <p className="text-blue-600 text-sm mt-1">Assign aides to schedule blocks</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onAddAide(); }}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
            disabled={aideScheduleBlocks.length === 0}
            title={aideScheduleBlocks.length === 0 ? 'Add schedule blocks first' : ''}
          >
            + Add Aide
          </button>
        </div>

        {expandedSections.aides && (
        <div className="p-6">
        {aideScheduleBlocks.length === 0 ? (
          <div className="text-center py-8 bg-amber-50 rounded-xl border-2 border-amber-200">
            <div className="text-4xl mb-3">⏰</div>
            <p className="text-amber-800 font-medium">Define schedule blocks first</p>
            <p className="text-amber-600 text-sm mt-1">Add time blocks above, then you can assign aides to them</p>
          </div>
        ) : aides.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-teal-50 to-green-50 rounded-xl border-2 border-teal-200">
            <div className="text-6xl mb-4">🙋</div>
            <p className="text-slate-600 text-lg font-medium">No aides yet</p>
            <p className="text-slate-500 text-sm mt-2">Click "+ Add Aide" to assign someone to your schedule blocks</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Sort aides: A Day before B Day */}
            {[...aides]
              .sort((a, b) => {
                if (a.dayType === 'A' && b.dayType === 'B') return -1;
                if (a.dayType === 'B' && b.dayType === 'A') return 1;
                return a.name.localeCompare(b.name);
              })
              .map(aide => {
              const assignedBlocks = aide.assignedBlocks || [];

              return (
                <div key={aide.id} className={`border-2 rounded-lg p-4 ${
                  aide.dayType === 'A'
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-purple-200 bg-purple-50'
                }`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className={`font-bold text-lg ${
                          aide.dayType === 'A' ? 'text-blue-800' : 'text-purple-800'
                        }`}>
                          {aide.name}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          aide.dayType === 'A'
                            ? 'bg-blue-200 text-blue-800'
                            : 'bg-purple-200 text-purple-800'
                        }`}>
                          {aide.dayType} Day
                        </span>
                        {aide.email && (
                          <span className="text-sm text-slate-500">{aide.email}</span>
                        )}
                      </div>

                      {/* Assigned Blocks */}
                      <div className="flex flex-wrap gap-2">
                        {assignedBlocks.length === 0 ? (
                          <span className="text-sm text-slate-500 italic">No blocks assigned</span>
                        ) : (
                          assignedBlocks.map(blockId => {
                            const block = getBlockDetails(blockId);
                            if (!block) return null;
                            const isMassDay = block.scheduleType === 'mass_day';
                            return (
                              <span
                                key={blockId}
                                className={`text-sm px-2 py-1 rounded ${
                                  isMassDay
                                    ? 'bg-violet-100 text-violet-700'
                                    : 'bg-teal-100 text-teal-700'
                                }`}
                              >
                                {block.name}: {formatTime12(block.startTime)} - {formatTime12(block.endTime)}
                                <span className="text-xs ml-1 opacity-70">
                                  ({block.library === 'elementary' ? 'E' : block.library === 'middle' ? 'M' : 'B'})
                                </span>
                              </span>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onEditAide(aide)}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDeleteAide(aide.id)}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h4 className="font-medium text-slate-700 mb-2">How Aide Coverage Works</h4>
          <div className="text-sm text-slate-600 space-y-1">
            <p><span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold bg-blue-200 text-blue-800 mr-2">A Day</span> Aides available on A days only</p>
            <p><span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold bg-purple-200 text-purple-800 mr-2">B Day</span> Aides available on B days only</p>
            <p><span className="inline-block w-3 h-3 bg-green-100 border border-green-300 rounded mr-2"></span> Regular schedule blocks show green shading on the calendar</p>
            <p><span className="inline-block w-3 h-3 bg-violet-100 border border-violet-300 rounded mr-2"></span> Mass Day blocks show purple shading on Mass Days</p>
          </div>
        </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default AidesTab;
